import { ErrorMessage, Field, type FieldProps, useField } from "formik"
import { faCalendar } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { format, isValid, parse, parseISO } from "date-fns"
import { Calendar } from "primereact/calendar"
import { Dropdown } from "primereact/dropdown"
import { InputMask } from "primereact/inputmask"
import { OverlayPanel } from "primereact/overlaypanel"
import { classNames } from "primereact/utils"
import { type FC, type FocusEvent, useEffect, useMemo, useReducer, useRef, useState } from "react"

const MAX_POSIBLE_AGE = 150
const DISPLAY_FORMAT = "MM/dd/yyyy"
const INPUT_MASK = "99/99/9999"

const BirthdateField: FC<Props> = ({ variant = "dropdown", ...props }) => {
  if (variant === "calendar") {
    return <CalendarBirthdateField {...props} />
  }

  return <DropdownBirthdateField {...props} />
}

const CalendarBirthdateField: FC<Omit<Props, "variant">> = (props) => {
  const { field: fieldPath, ...restProps } = props

  return (
    <Field name={fieldPath}>
      {(fieldProps: FieldProps) => (
        <CalendarBirthdateInput
          {...restProps}
          fieldPath={fieldPath}
          field={fieldProps.field}
          meta={fieldProps.meta}
          form={fieldProps.form}
        />
      )}
    </Field>
  )
}

const CalendarBirthdateInput: FC<
  Omit<Props, "variant" | "field"> & { fieldPath: string } & Pick<FieldProps, "field" | "meta" | "form">
> = ({
  fieldPath,
  label,
  className,
  inputClassName,
  horizontal,
  field: { name, value, onBlur: fieldOnBlur },
  meta: { touched, error },
  form: { setFieldValue, setFieldTouched },
}) => {
  const overlayRef = useRef<OverlayPanel>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [overlayWidth, setOverlayWidth] = useState<number>()
  const [inputValue, setInputValue] = useState("")
  const [isFocused, setIsFocused] = useState(false)
  const showMask = isFocused || !!inputValue
  const maxDate = useMemo(() => new Date(), [])
  const minDate = useMemo(() => {
    const date = new Date()
    date.setFullYear(date.getFullYear() - MAX_POSIBLE_AGE)
    return date
  }, [])
  const defaultViewDate = useMemo(() => {
    const date = new Date()
    date.setFullYear(date.getFullYear() - 30)
    return date
  }, [])

  const calendarValue = useMemo(() => toCalendarValue(value), [value])

  useEffect(() => {
    setInputValue(toDisplayValue(value))
  }, [value])

  useEffect(() => {
    if (!showMask || !isFocused) return

    requestAnimationFrame(() => {
      containerRef.current?.querySelector<HTMLInputElement>(".ch-datepicker-input")?.focus()
    })
  }, [showMask, isFocused])

  const commitParsedDate = (parsed: Date | null) => {
    setFieldValue(fieldPath, parsed ? toFormValue(parsed) : "")
    if (parsed) {
      setInputValue(format(parsed, DISPLAY_FORMAT))
    }
  }

  const inputClassNames = classNames(
    "ch-datepicker-input p-inputtext p-component p-inputtext-sm w-full",
    { "p-invalid": touched && error, horizontal },
    inputClassName,
  )

  const handleInputBlur = (rawValue: string, e: FocusEvent<HTMLInputElement>) => {
    const parsed = parseMaskedBirthdate(rawValue, minDate, maxDate)
    if (parsed) {
      commitParsedDate(parsed)
    } else if (!rawValue.replace(/[_/\s]/g, "")) {
      setFieldValue(fieldPath, "")
      setInputValue("")
    }
    fieldOnBlur(e)
    setFieldTouched(fieldPath, true, false)
    setIsFocused(false)
  }

  return (
    <div className={classNames("field flex flex-col relative", className)}>
      {label && (
        <label htmlFor={name} className="font-medium text-gray-500 text-sm mb-2">
          {label}
        </label>
      )}
      <div ref={containerRef} className="relative w-full ch-datepicker">
        {showMask ? (
          <InputMask
            id={name}
            name={name}
            mask={INPUT_MASK}
            autoComplete="off"
            placeholder="MM/DD/YYYY"
            value={inputValue}
            onFocus={() => setIsFocused(true)}
            onChange={(e) => {
              const nextValue = e.value ?? ""
              setInputValue(nextValue)
              commitParsedDate(parseMaskedBirthdate(nextValue, minDate, maxDate))
            }}
            onBlur={(e) => handleInputBlur(e.target.value, e)}
            className={inputClassNames}
          />
        ) : (
          <input
            id={name}
            name={name}
            type="text"
            autoComplete="off"
            placeholder="MM/DD/YYYY"
            readOnly={false}
            onFocus={() => setIsFocused(true)}
            className={inputClassNames}
          />
        )}
        <button
          type="button"
          aria-label="Open calendar"
          className="ch-datepicker-icon"
          onClick={(e) => {
            if (containerRef.current) {
              setOverlayWidth(containerRef.current.getBoundingClientRect().width)
            }
            overlayRef.current?.toggle(e, containerRef.current ?? e.currentTarget)
          }}
        >
          <FontAwesomeIcon icon={faCalendar} className="h-4 w-4" />
        </button>
        <OverlayPanel
          ref={overlayRef}
          dismissable
          appendTo={typeof document === "undefined" ? undefined : document.body}
          className="ch-datepicker-overlay"
          style={overlayWidth ? { width: overlayWidth } : undefined}
        >
          <Calendar
            inline
            value={calendarValue}
            viewDate={calendarValue ?? defaultViewDate}
            onChange={(e) => {
              const selectedDate = e.value as Date | null
              if (!selectedDate || !isValid(selectedDate)) return

              commitParsedDate(selectedDate)
              overlayRef.current?.hide()
            }}
            maxDate={maxDate}
            minDate={minDate}
            className="ch-datepicker-panel w-full"
          />
        </OverlayPanel>
      </div>
      <div className="flex items-start p-error h-4 mt-1">
        {touched && error && <ErrorMessage name={fieldPath}>{(msg) => <small>{msg}</small>}</ErrorMessage>}
      </div>
    </div>
  )
}

type BirthdateFieldVariant = "dropdown" | "calendar"

type Props = {
  field: string
  label?: string
  className?: string
  inputClassName?: string
  horizontal?: boolean
  variant?: BirthdateFieldVariant
}

const DropdownBirthdateField: FC<Omit<Props, "variant">> = ({ field, label, className, horizontal }) => {
  const [, { value }, { setValue }] = useField<string>(field)
  const { months, days, year, month, day, updateYear, updateMonth, updateDay } = useStateReducer(value ?? "")

  const years = useMemo(() => {
    const minYear = new Date().getFullYear() - MAX_POSIBLE_AGE
    const result = Array.from(new Array(MAX_POSIBLE_AGE + 1), (_, i) => ({
      code: i + minYear,
      label: (i + minYear).toString(),
    }))
    return result.sort((a, b) => b.code - a.code)
  }, [])

  const updateValue = (newYear: number, newMonth: number, newDay: number) => {
    const newDate = parse(`${newMonth}-${newDay}-${newYear}`, "M-d-yyyy", new Date())

    if (!isValid(newDate)) {
      setValue("")
    } else {
      setValue(format(newDate, "yyyy-MM-dd"))
    }
  }

  return (
    <Field name={field}>
      {({ field: { name }, meta: { touched, error } }: FieldProps) => (
        <div className={classNames("field flex flex-col relative", className)}>
          {label && (
            <label htmlFor={name} className="font-medium text-gray-500 text-sm mb-2">
              {label}
            </label>
          )}
          <div className="flex justify-between gap-4">
            <Dropdown
              optionLabel="label"
              optionValue="code"
              value={month}
              options={months}
              className={classNames("flex-1 p-inputtext-sm h-10", {
                "p-invalid": touched && !month,
                horizontal,
              })}
              onChange={(e) => {
                updateMonth(e.value)
                updateValue(year, e.value, day)
              }}
              placeholder="Month"
            />
            <Dropdown
              options={days}
              value={day}
              onChange={(e) => {
                updateDay(e.value)
                updateValue(year, month, e.value)
              }}
              className={classNames("flex-1 p-inputtext-sm h-10", {
                "p-invalid": touched && !day,
                horizontal,
              })}
              optionLabel="label"
              optionValue="code"
              placeholder="Day"
            />
            <Dropdown
              options={years}
              value={year}
              onChange={(e) => {
                updateYear(e.value)
                updateValue(e.value, month, day)
              }}
              className={classNames("flex-1 p-inputtext-sm h-10", {
                "p-invalid": touched && !year,
                horizontal,
              })}
              optionLabel="label"
              optionValue="code"
              placeholder="Year"
            />
          </div>
          <div className="flex items-start p-error h-4 mt-1">
            {touched && error && <ErrorMessage name={field}>{(msg) => <small>{msg}</small>}</ErrorMessage>}
          </div>
        </div>
      )}
    </Field>
  )
}

const toFormValue = (date: Date | null | undefined) => (date && isValid(date) ? format(date, "yyyy-MM-dd") : "")

const toDisplayValue = (value: string | undefined) => {
  if (!value) return ""
  const parsed = parseISO(value)
  return isValid(parsed) ? format(parsed, DISPLAY_FORMAT) : ""
}

const toCalendarValue = (value: string | undefined) => {
  if (!value) return null
  const parsed = parseISO(value)
  return isValid(parsed) ? parsed : null
}

const parseMaskedBirthdate = (masked: string | null | undefined, minDate: Date, maxDate: Date) => {
  if (!masked) return null

  const cleaned = masked.replace(/_/g, "").trim()
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(cleaned)) return null

  const parsed = parse(cleaned, DISPLAY_FORMAT, new Date())
  if (!isValid(parsed) || parsed < minDate || parsed > maxDate) return null

  return parsed
}

const getMonths = (year: number) => {
  const monthList = Array.from({ length: 12 }, (_, i) => ({
    code: i + 1,
    label: new Date(0, i).toLocaleString("en-US", { month: "long" }),
  }))

  const currentDate = new Date()

  if (year === currentDate.getFullYear()) {
    return monthList.filter((month) => month.code <= currentDate.getMonth() + 1)
  }

  return monthList
}

const getDays = (year: number, month: number) => {
  let totalDays = new Date(2000, 1, 0).getDate()

  if (year && month) {
    totalDays = new Date(year, month, 0).getDate()
  }

  return Array.from({ length: totalDays }, (_, i) => ({
    code: i + 1,
    label: (i + 1).toString(),
  }))
}

const getInitialState = (year: number, month: number, day: number): State => ({
  months: getMonths(year),
  days: getDays(year, month),
  year,
  month,
  day,
})

const reducer = (state: State, { type, payload }: { type: string; payload: Option[] | number }) => {
  switch (type) {
    case "changeYear": {
      const year = payload as number
      const newDayYear = state.day > getDays(year, state.month).length ? NaN : state.day
      return { ...state, months: getMonths(year), year, days: getDays(year, state.month), day: newDayYear }
    }
    case "changeMonth": {
      const month = payload as number
      const newDayMonth = state.day > getDays(state.year, month).length ? NaN : state.day
      return { ...state, days: getDays(state.year, month), month, day: newDayMonth }
    }
    case "changeDay":
      return { ...state, day: payload as number }
    default:
      return state
  }
}

const useStateReducer = (dateString: string) => {
  const parsedDate = dateString ? parseISO(dateString) : new Date(Number.NaN)
  const initialDate = isValid(parsedDate) ? parsedDate : new Date(Number.NaN)

  const [{ months, days, year, month, day }, dispatch] = useReducer(
    reducer,
    getInitialState(initialDate.getFullYear(), initialDate.getMonth() + 1, initialDate.getDate()),
  )

  const updateYear = (nextYear: number) => dispatch({ type: "changeYear", payload: nextYear })
  const updateMonth = (nextMonth: number) => dispatch({ type: "changeMonth", payload: nextMonth })
  const updateDay = (nextDay: number) => dispatch({ type: "changeDay", payload: nextDay })

  return { months, days, year, month, day, updateYear, updateMonth, updateDay }
}

type State = {
  months: Option[]
  days: Option[]
  year: number
  month: number
  day: number
}

type Option = {
  code: number
  label: string
}

export { BirthdateField }
